import Backbone from "backbone";
import RSVP from "rsvp";
import ParagraphLayoutBehavior from "../ParagraphLayoutBehavior";
import ParagraphBoundaryPreference from "../ParagraphBoundaryPreference";
import Utils from "models/helper/Utils";
import VoiceActivityDetectionHelper from "models/audioEditor/VoiceActivityDetectionHelper";
import VoiceActivityDetectionManager from "models/audioEditor/VoiceActivityDetectionManager";
import BreathingAnalysisHelper from "models/audioEditor/BreathingAnalysisHelper";
import { WrappedNodePropertyCategory, getPropertyCategory } from "models/audioEditor/sequencerNodes/WrappedNodeChangeEnums";


const CLASSNAME =  "SequencerNode";
const SequencerNode =  Backbone.Model.extend({
    idAttribute: "id",


    constructor: function(attributes, options) {
        this._nextSequencerNode = null;
        this._previousSequencerNode = null;
        this._audioSegment = attributes.audioSegment;
        this._startWrapNodes = [];
        this._endWrapNodes = [];
        this._audioFilters = [];
        this._uniqueNameToBuiltInFilter = {};
        Backbone.Model.apply(this, [attributes, options]);
        if(attributes.sequencer){
            attributes.sequencer.addToSequencerNodeCreated(this);
            this.setSequencer(attributes.sequencer);
        }
        this._listenToChangesOnAudioSegment();
        this._enableChangeBroadcasting();
    },

    getAudioSegment : function(){
        return this._audioSegment;
    },

    getParagraphBoundaryPreference : function(){
        return ParagraphBoundaryPreference.normalize(this.get("paragraphBoundaryPreference"));
    },

    setParagraphBoundaryPreference : function(paragraphBoundaryPreference){
        const normalizedPreference = ParagraphBoundaryPreference.normalize(paragraphBoundaryPreference);
        if(normalizedPreference){
            this.set("paragraphBoundaryPreference", normalizedPreference);
        }else{
            this.unset("paragraphBoundaryPreference");
        }
        return this.getParagraphBoundaryPreference();
    },

    clearParagraphBoundaryPreference : function(){
        this.unset("paragraphBoundaryPreference");
        return null;
    },

    getSerializedData : function(){
        const stateToReturn  = this.toJSON();
        stateToReturn._cid =  this.cid;
        if(stateToReturn.audioSegment && stateToReturn.audioSegment.getSerializedData){
            stateToReturn.audioSegment = stateToReturn.audioSegment.getSerializedData();
        }
        stateToReturn._startWrapNodesCid = [];
        stateToReturn._endWrapNodesCid = [];
        if(this.getStartWrapNodes() && this.getStartWrapNodes().length >0){
            for(let i =0; i < this.getStartWrapNodes().length; i++){
                const startWrapNode = this.getStartWrapNodes()[i];
                if(startWrapNode){
                    stateToReturn._startWrapNodesCid.push(startWrapNode.cid);
                }
            }
        }
        if(this.getEndWrapNodes() && this.getEndWrapNodes().length >0){
            for(let i =0; i < this.getEndWrapNodes().length; i++){
                const endWrapNode = this.getEndWrapNodes()[i];
                if(endWrapNode){
                    stateToReturn._endWrapNodesCid.push(endWrapNode.cid);
                }
            }
        }
        stateToReturn.audioFilters = [];
        for(let i =0; i < this._audioFilters.length; i++){
            const af = this._audioFilters[i];
            stateToReturn.audioFilters.push(af.getSerializedData());
        }
        stateToReturn.builtInFiltersUniqueNames = [];
        for(let uniquename in this._uniqueNameToBuiltInFilter ){
            stateToReturn.builtInFiltersUniqueNames.push(uniquename);
        }


        delete stateToReturn.sequencer;
        return stateToReturn;
    },

    restoreFromSerializedData: function(serializedData, audioSegmentFactoryInstance, audioFilterFactoryInstance, additionalSpeakerInfoArray){
        const promises = []
        this.set(serializedData);
        this._serializedData = serializedData;
        promises.push(audioSegmentFactoryInstance.createAudioSegmentFromSerializedData(serializedData.audioSegment, additionalSpeakerInfoArray, this.getSequencer()?this.getSequencer().generateSpeakerLabelIfDuplicate.bind(this): null).then((function(audioSegmentInstanceCreated){
            this.setAudioSegment(audioSegmentInstanceCreated);
        }).bind(this)));
        
        this._startWrapNodes = [];
        this._endWrapNodes = [];
        this._audioFilters = [];
        this._uniqueNameToBuiltInFilter = {};
        const nameToAudioFilter = {};
        if(serializedData.audioFilters){
            for(let i =0; i < serializedData.audioFilters.length; i++){
                const afSerializedData = serializedData.audioFilters[i];
                const af = audioFilterFactoryInstance.createAudioFilterFromSerializedData(afSerializedData);
                if(af.getName()){
                    nameToAudioFilter[af.getName()] =  af;
                }
                this._audioFilters.push(af);
            }
        }
        if(serializedData.builtInFiltersUniqueNames){
            for(let i =0; i < serializedData.builtInFiltersUniqueNames.length; i++){
                const builtInFilterName = serializedData.builtInFiltersUniqueNames[i];
                if(nameToAudioFilter[builtInFilterName]){
                    this._uniqueNameToBuiltInFilter[builtInFilterName] = nameToAudioFilter[builtInFilterName];
                }
            }
        }
        return RSVP.Promise.all(promises);
    },

    afterAllInstancesCreatedFromSerializedData : function(serializedData, sequencerNodeCidToSequencerNodeMap){
        if(serializedData._startWrapNodesCid){
            for(let i =0; i < serializedData._startWrapNodesCid.length; i++){
                const nodeCid = serializedData._startWrapNodesCid[i];
                if(nodeCid){
                    const sequencerNode = sequencerNodeCidToSequencerNodeMap[nodeCid];
                    if(sequencerNode){
                        this.addStartWrapNode(sequencerNode);
                    }
                }
            }
        }
        if(serializedData._endWrapNodesCid){
            for(let i =0; i < serializedData._endWrapNodesCid.length; i++){
                const nodeCid = serializedData._endWrapNodesCid[i];
                if(nodeCid){
                    const sequencerNode = sequencerNodeCidToSequencerNodeMap[nodeCid];
                    if(sequencerNode){
                        this.addEndWrapNode(sequencerNode);
                    }
                }
            }
        }
        const nextNode = sequencerNodeCidToSequencerNodeMap[serializedData._nextSequenceNodeCid];
        if(nextNode){
            this.setNext(nextNode);
        }
    },

    _stopListeningToAudioSegmentChanges : function(){
        const audioSegment = this.get("audioSegment");
        if(audioSegment && audioSegment instanceof Backbone.Model){
            this.stopListening(audioSegment, "change");
        }
    },

    _listenToChangesOnAudioSegment : function(){
        const audioSegment = this.get("audioSegment");
        if(audioSegment){
            this.listenTo(audioSegment, 'change', (function(){
                this.trigger("change:audioSegment", this);
            }).bind(this));
        }
    },

    setAudioSegment : function(newAudioSegment){
        this._stopListeningToAudioSegmentChanges();
        this._audioSegment = newAudioSegment;
        this.set("audioSegment",newAudioSegment);
        this._listenToChangesOnAudioSegment();
        
    },

    cloneJSON: function(){
        const objJson = {};
        objJson.sequencer = this.get("sequencer");
        objJson.applyCrossfadeAtTheBegining = this.get("applyCrossfadeAtTheBegining");
        objJson.applyCrossfadeAtTheEnd = this.get("applyCrossfadeAtTheEnd");
        objJson.applyCrossfadeAtTheBeginingDurationInMilliseconds =  this.get("applyCrossfadeAtTheBeginingDurationInMilliseconds");
        objJson.applyCrossfadeAtTheBeginingDurationInMilliseconds =  this.get("applyCrossfadeAtTheEndDurationInMilliseconds");
        objJson.audioSegment = this.getAudioSegment().clone();
        const paragraphBoundaryPreference = this.getParagraphBoundaryPreference();
        if(paragraphBoundaryPreference){
            objJson.paragraphBoundaryPreference = paragraphBoundaryPreference;
        }
        objJson.type = SequencerNode.CLASSNAME;
        return objJson;
    },

    getClassifiedAudioCategories: function(){
        if(this.getAudioSegment() && this.getAudioSegment().getStartTime() >=0 && this.getAudioSegment().getEndTime() >=0 && this.getAudioSegment().getAudioUrl()){
            const classificationResults = this.getSequencer().getAudioBufferCache().getClassificationResultsForAudioUrl(this.getAudioSegment().getAudioUrl());
            if(classificationResults){
                return classificationResults.getClassifications(this.getAudioSegment().getStartTime(), this.getAudioSegment().getEndTime());
            }
        }
        return [];
    },

    /**
     * Get voice activity detection data for this node's audio segment.
     *
     * @param {string} vadConfigType - VAD configuration type:
     *   - SILENCE_DETECTION: Aggressive silence detection (0.8 threshold) - use for finding clear silences
     *   - WORD_BOUNDARY: Sensitive detection (0.3 threshold) - use for breathing analysis and unclear speech
     *   - BALANCED: Balanced detection (0.5 threshold) - general purpose default
     * @returns {Array|null} VAD results array or null if not cached
     * @deprecated Use getVoiceActivityInfo() for correct spelling
     */
    getVoiceAcitivityInfo : function(vadConfigType){
        return this.getSequencer().getAudioBufferCache().getVoiceActivityDetectionResultsForAudioUrl(
            this.getAudioSegment().getAudioUrl(),
            vadConfigType
        );
    },

    /**
     * Get voice activity detection data for this node's audio segment.
     * Alias for getVoiceAcitivityInfo() with correct spelling.
     *
     * @param {string} vadConfigType - VAD configuration type:
     *   - SILENCE_DETECTION: Aggressive silence detection (0.8 threshold) - use for finding clear silences
     *   - WORD_BOUNDARY: Sensitive detection (0.3 threshold) - use for breathing analysis and unclear speech
     *   - BALANCED: Balanced detection (0.5 threshold) - general purpose default
     * @returns {Array|null} VAD results array or null if not cached
     */
    getVoiceActivityInfo : function(vadConfigType){
        return this.getVoiceAcitivityInfo(vadConfigType);
    },

    /**
     * Check if this node contains voice activity within its time boundaries.
     * 
     * @param {string} vadConfigType - VAD configuration type (defaults to BALANCED):
     *   - SILENCE_DETECTION: Use for aggressive silence detection
     *   - WORD_BOUNDARY: Use for sensitive detection of unclear speech
     *   - BALANCED: General purpose detection
     * @returns {boolean} True if voice activity is detected within this node's time range
     */
    containsVoiceActivity : function(vadConfigType){
        const vadManager = VoiceActivityDetectionManager.getInstance();
        const configType = vadConfigType || vadManager.getVadConfigs().BALANCED;
        return vadManager.containsVoiceActivity(
            this.getVoiceAcitivityInfo(configType),
            this.getAudioSegment().getStartTime(), 
            this.getAudioSegment().getEndTime()
        );
    },

    getEnhancedCutPointAnalysisInfo : function(){
        const audioUrl = this.getAudioSegment().getAudioUrl();
        if (!audioUrl) return null;
        
        return this.getSequencer().getAudioBufferCache().getEnhancedCutPointAnalysisResultsForAudioUrl(audioUrl);
    },

    /**
     * Find potential breathing pauses in this node using conservative VAD + duration heuristics.
     * Follows the same elegant pattern as containsVoiceActivity().
     * 
     * @param {Object} options - Detection options
     * @param {string} vadConfigType - VAD configuration type (defaults to WORD_BOUNDARY for sensitive breathing detection)
     * @returns {Object|null} Conservative potential breathing analysis or null if no cached VAD data
     */
    findPotentialBreathingPauses : function(options = {}, vadConfigType) {
        const vadManager = VoiceActivityDetectionManager.getInstance();
        const configType = vadConfigType || vadManager.getVadConfigs().WORD_BOUNDARY; // Use sensitive config for breathing
        
        return BreathingAnalysisHelper.getInstance().findPotentialBreathingPausesFromVADArray(
            this.getVoiceAcitivityInfo(configType),
            this.getAudioSegment().getStartTime(),
            this.getAudioSegment().getEndTime(),
            options
        );
    },

    /**
     * Check if this node might contain a breathing pause (conservative heuristic).
     * Follows the same elegant pattern as containsVoiceActivity().
     * 
     * @param {string} likelihood - Minimum likelihood threshold ('low', 'low-medium', 'medium')
     * @param {string} vadConfigType - VAD configuration type (defaults to WORD_BOUNDARY for sensitive breathing detection)
     * @returns {boolean} True if this node might contain a breathing pause
     */
    mightContainBreathingPause : function(likelihood = 'low-medium', vadConfigType) {
        const vadManager = VoiceActivityDetectionManager.getInstance();
        const configType = vadConfigType || vadManager.getVadConfigs().WORD_BOUNDARY; // Use sensitive config for breathing
        
        const analysis = this.findPotentialBreathingPauses({
            conservativeMode: true
        }, configType);
        
        if (!analysis || analysis.totalPotentialBreaths === 0) return false;
        
        // Check if any potential breaths meet the likelihood threshold
        const thresholdOrder = ['low', 'low-medium', 'medium'];
        const minIndex = thresholdOrder.indexOf(likelihood);
        
        return analysis.potentialBreaths.some(breath => {
            const breathIndex = thresholdOrder.indexOf(breath.likelihood);
            return breathIndex >= minIndex;
        });
    },

    /**
     * Returns the duration in milliseconds for which a visual gap filler
     * (e.g., black frame) should be displayed for this node during video rendering.
     *
     * This is used by VisualElementFactory when creating scenes for Layout wraps
     * to determine if an audio-gap segment is needed. Audio is still playing during
     * this time - only the visual needs a placeholder.
     *
     * - Returns 0 if the node has video content (no gap needed)
     * - Returns the node's audio duration if it's audio-only content
     * - Override in subclasses for special handling (e.g., WrapSequencerNode)
     *
     * @param {Object} schedulingInfo - Optional scheduling info for accurate duration
     *                                  (preferred over audioSegment.getDuration() for
     *                                  inserted audio nodes which return 0)
     * @returns {number} Duration in milliseconds for visual gap filler
     */
    getVisualGapDurationMs: function(schedulingInfo) {
        const audioSegment = this.getAudioSegment();
        const hasVideo = audioSegment?.getHasVideo?.() ?? false;

        if (hasVideo) {
            return 0; // Has video, no visual gap needed
        }

        // Prefer scheduling info for accuracy
        // AudioSegment.getDuration() returns 0 for inserted audio nodes (they lack start/end times)
        // SchedulingInfo.getDuration() uses the actual audio buffer duration
        if (schedulingInfo?.getDuration) {
            return schedulingInfo.getDuration();
        }

        return audioSegment?.getDuration?.() || 0;
    },

    /**
     * Returns whether this node can be dragged on the timeline.
     * Override in subclasses that support drag-and-drop (e.g., WrapSequencerNode).
     *
     * @returns {boolean} False for base SequencerNode, overridden in WrapSequencerNode
     */
    isDraggable: function() {
        return false;
    },

    /**
     * Returns whether this node affects video output (visual overlays, effects, etc.).
     * Override in subclasses that render visual elements (e.g., visual wrap nodes).
     *
     * @returns {boolean} False for base SequencerNode, overridden in visual wrap nodes
     */
    affectsVideoOutput: function() {
        return false;
    },

    /**
     * Returns an array of video media URLs that should be cached for this node.
     * Override in subclasses that have video content that needs caching.
     *
     * This polymorphic method allows each node type to declare its own cacheability,
     * following the same pattern as affectsVideoOutput(), isDraggable(), etc.
     *
     * Examples:
     * - VideoSequencerNode: Returns the video URL from its audio segment
     * - StartFloatingOverlayWrapSequencerNode: Returns the overlay video URL
     * - StartFullscreenOverlayWrapSequencerNode: Returns the overlay video URL
     * - Other nodes: Return empty array (no video to cache)
     *
     * @returns {Array<string>} Array of video URLs to cache (empty by default)
     */
    getCacheableVideoMediaUrls: function() {
        const audioSegment = this.getAudioSegment();
        if (audioSegment && audioSegment.getHasVideo && audioSegment.getHasVideo()) {
            const url = audioSegment.getAudioUrl ? audioSegment.getAudioUrl() : null;
            return url ? [url] : [];
        }
        return [];
    },

    /**
     * Returns whether this node is a wrap node (Start/End pair that wraps content).
     * Override in WrapSequencerNode to return true.
     *
     * @returns {boolean} False for base SequencerNode, true for WrapSequencerNode
     */
    isWrapNode: function() {
        return false;
    },

    /**
     * Returns whether this node is a start marker of a wrap pair.
     * Override in StartWrapSequencerNode to return true.
     *
     * @returns {boolean} False for most nodes, true for StartWrapSequencerNode
     */
    isStartMarker: function() {
        return false;
    },

    /**
     * Returns whether this node is an end marker of a wrap pair.
     * Override in EndWrapSequencerNode to return true.
     *
     * @returns {boolean} False for most nodes, true for EndWrapSequencerNode
     */
    isEndMarker: function() {
        return false;
    },

    /**
     * Whether this node should be auto-selected when initially created.
     * Override in subclasses that should auto-select (e.g., Title, Overlay, Zoom, etc.).
     *
     * @returns {boolean} False by default, override to return true for auto-select
     */
    shouldAutoSelectOnCreation: function() {
        return false;
    },

    /**
     * The node that should actually be selected (for wrap nodes, returns start node).
     * Override in EndWrapSequencerNode to return the start node.
     *
     * @returns {SequencerNode} This node by default
     */
    getAutoSelectTarget: function() {
        return this;
    },

    /**
     * Hint for inspector about what custom behavior to execute after selection.
     * Examples: 'focusTitleText', 'focusMediaPicker', null
     * Override in subclasses that need custom post-selection behavior.
     *
     * @returns {string|null} Null by default, override to return a hint string
     */
    getPostSelectionHint: function() {
        return null;
    },

    // ============================================================
    // PROPERTY CATEGORY DECLARATIONS
    // These methods allow nodes to declare which of their setter methods
    // affect timeline duration or visual output. This enables the
    // UpdateSequencerNodePropertyOperation to trigger appropriate events
    // (durationChanged, visualPropertyChanged) when properties are modified.
    // Override in subclasses to add node-specific properties.
    // ============================================================

    /**
     * Returns a Set of setter method names that affect timeline duration.
     * When these properties change via UpdateSequencerNodePropertyOperation,
     * the "durationChanged" event will be triggered on the Sequencer.
     *
     * Override in subclasses to add node-specific duration-affecting properties.
     * Call the parent implementation to include inherited properties.
     *
     * Example override:
     *   getDurationPropertyNames: function() {
     *       const parentSet = SequencerNode.prototype.getDurationPropertyNames.call(this);
     *       return new Set([...parentSet, 'setMyDurationProperty']);
     *   }
     *
     * @returns {Set<string>} Set of setter method names that affect duration
     */
    getDurationPropertyNames: function() {
        return new Set();
    },

    /**
     * Returns a Set of setter method names that affect visual output.
     * When these properties change via UpdateSequencerNodePropertyOperation,
     * the "visualPropertyChanged" event will be triggered on the Sequencer.
     *
     * Override in subclasses to add node-specific visual-affecting properties.
     * Call the parent implementation to include inherited properties.
     *
     * Example override:
     *   getVisualPropertyNames: function() {
     *       const parentSet = SequencerNode.prototype.getVisualPropertyNames.call(this);
     *       return new Set([...parentSet, 'setMyVisualProperty']);
     *   }
     *
     * @returns {Set<string>} Set of setter method names that affect visual output
     */
    getVisualPropertyNames: function() {
        return new Set();
    },

    afterInstanceCreatedFromConstructorForCloning : function(createdClonedInstance, sequencerNodeCidToCloneNodeMap){

    },

    /*cloneUntil : function(sequencerNodeToStopCloningAt, sequencerNodeCidToCloneNodeMap){
        if(!sequencerNodeCidToCloneNodeMap){
            sequencerNodeCidToCloneNodeMap = {}
        }
        const existingClone  = sequencerNodeCidToCloneNodeMap[this.cid];
        if(existingClone){
            return existingClone;
        }
        const nextNode = this.getNext();
        console.log("type ");
        const clonedNextNode = sequencerNodeToStopCloningAt === this || !nextNode? null: nextNode.cloneUntil(sequencerNodeToStopCloningAt, sequencerNodeCidToCloneNodeMap)
        const contructorClass = this.getConstructor();
        const toReturn =  new contructorClass(this.cloneJSON(),null, true, this, sequencerNodeCidToCloneNodeMap);
        sequencerNodeCidToCloneNodeMap[this.cid] = toReturn;
        this.afterInstanceCreatedFromConstructorForCloning(toReturn, sequencerNodeCidToCloneNodeMap);
        if(clonedNextNode){
            toReturn.insertBefore(clonedNextNode);
        }
        return toReturn;
    },*/

    getAudioSegmentJson : function(){
        return this._audioSegment? this._audioSegment.toJSON(): null;
    },

    setAudioSegmentJson : function(newAudioSegmentJson){
        this._audioSegment? this._audioSegment.set(newAudioSegmentJson): null;
    },

    getCid : function(){
        return this.cid;
    },

    setSequencer : function(sequencer){
        if(this.getSequencer() && this.getSequencer().getSequencerSettings()){
            //this.stopListening(this.getSequencer().getSequencerSettings());
        }
        if(sequencer){
            this.set("sequencer", sequencer);
            /*this.listenTo(this.getSequencer().getSequencerSettings(), "change:showGapsBetweenWords change:showIgnoredContent",  (function(){
                this.trigger("sequencerViewSettingsChange", this);
            }).bind(this));*/
        }
    },

    getSequencer : function(){
        return this.get("sequencer");
    },

    addToSequencer: function(){
        this.setAddedToSequencer(true);
        this.getSequencer().add(this);
    },

    removeFromSequencer: function(){
        this.setAddedToSequencer(false);
        this.getSequencer().remove(this);
    },

    setInsertedByPasting : function(insertedByPasting){
        this.set("insertedByPasting", insertedByPasting)
    },

    getInsertedByPasting: function(){
        return this.get("insertedByPasting");
    },

    isThereEditBoundaryAtBeginning: function(){
        return !!this.getSequencerNodeToCrossfadeAtTheBeginingWith(true);
    },

    isThereEditBoundaryAtBeginningAndIsSegmentLargeEnoughForCrossfade : function(){
        return !!this.getSequencerNodeToCrossfadeAtTheBeginingWith(false);
    },

    getSequencerNodeToCrossfadeAtTheBeginingWith : function(doNotCheckMinimumDurationForNeighborNode){
        if(!this.canLetFadeIfNeighborNodesAreDifferentFromOriginal()){
            return null;
        }
        let previousSequencerNodeToCrossfeadeAtTheEndWith = null;
        let node = this.getPrevious();
        if(this.getAudioSegment() && this.getAudioSegment().getTranscribedAudioInstance()){
            const minimumDurationInMillisecondsForCrossfade  = this.getApplyCrossfadeAtTheBeginingDurationInMilliseconds() + this.getApplyCrossfadeAtTheEndDurationInMilliseconds();
            if(!doNotCheckMinimumDurationForNeighborNode && (minimumDurationInMillisecondsForCrossfade > 0 && minimumDurationInMillisecondsForCrossfade >= this.getAudioSegment().getDuration())){
                return null;
            }
            while(node != null){
                if(node.getAudioSegment() && node.getAudioSegment().getDuration() > 0){
                    if(node.needAutoCrossfadeIfNeighborNodesAreDifferentFromOriginal()){
                        previousSequencerNodeToCrossfeadeAtTheEndWith = node;
                    }
                    node = null;
                }else{
                    node = node.getPrevious();
                }
                
            }
            if(previousSequencerNodeToCrossfeadeAtTheEndWith){
                const audioSegment = previousSequencerNodeToCrossfeadeAtTheEndWith.getAudioSegment();
                const areNextToEachOther = this.getAudioSegment().getTranscribedAudioInstance().areAudioSegmentTheClosestToEachOther(audioSegment, this.getAudioSegment());
                if(!areNextToEachOther){
                    if(!doNotCheckMinimumDurationForNeighborNode && (minimumDurationInMillisecondsForCrossfade > 0 && minimumDurationInMillisecondsForCrossfade >= audioSegment.getDuration())){
                        return null;
                    }
                    return previousSequencerNodeToCrossfeadeAtTheEndWith;
                }
            }
        }else{
            return null;
        }

    },

    

    isThereContinuityOnLeft : function(){
        const previousNode = this.getPreviousSequencerNodeWithDuration();
        const continuityWithPreviousNodeWithDuration =  previousNode && previousNode.getAudioSegment() && this.getAudioSegment().getTranscribedAudioInstance()? this.getAudioSegment().getTranscribedAudioInstance().areAudioSegmentTheClosestToEachOther(previousNode.getAudioSegment(), this.getAudioSegment()):true;
        if(!continuityWithPreviousNodeWithDuration){
            return continuityWithPreviousNodeWithDuration;
        }else{
            if(previousNode && !this.getSequencer().isSequencerNodeVisible(previousNode)){
                return previousNode.isThereContinuityOnLeft();
            }
        }
        return true;
    },

    isThereContinuityOnRight : function(){
        const nextNode = this.getNextSequencerNodeWithDuration();
        const continuityWithNextNodeWithDuration = nextNode && nextNode.getAudioSegment() && this.getAudioSegment().getTranscribedAudioInstance()? this.getAudioSegment().getTranscribedAudioInstance().areAudioSegmentTheClosestToEachOther(this.getAudioSegment(), nextNode.getAudioSegment()) : true;
        if(!continuityWithNextNodeWithDuration){
            return continuityWithNextNodeWithDuration;
        }else{
            if(nextNode && !this.getSequencer().isSequencerNodeVisible(nextNode)){
                return nextNode.isThereContinuityOnRight();
            }
        }
        return true;
    },



    getNextSequencerNodeWithDuration : function(){
        /*const nextNode = this.getNext();
        const nodeToReturn = null;
        while(nextNode != null && !nodeToReturn){
            if(nextNode.getAudioSegment() && nextNode.getAudioSegment().getDuration() > 0){
                nodeToReturn = nextNode;
            }else{
                nextNode = nextNode.getNext();
            }

        }
        return nodeToReturn;*/
        return this.getNextNodeMatchingCondition((function(nextNode){
            return nextNode.getAudioSegment() && nextNode.getAudioSegment().getDuration() > 0
        }).bind(this));
    },

    getPreviousSequencerNodeWithDuration : function(){
        /*const previousNode = this.getPrevious();
        const nodeToReturn = null;
        while(previousNode != null && !nodeToReturn){
            if(previousNode.getAudioSegment() && previousNode.getAudioSegment().getDuration() > 0){
                nodeToReturn = previousNode;
            }else{
                previousNode = previousNode.getPrevious();
            }

        }
        return nodeToReturn;*/
        return this.getPreviousNodeMatchingCondition((function(previousNode){
            return previousNode.getAudioSegment() && previousNode.getAudioSegment().getDuration() > 0
        }).bind(this));
    },

    isThereEditBoundaryAtEnd: function(){
        return !!this.getSequencerNodeToCrossfadeAtTheEndWith(true);
    },

    isThereEditBoundaryAtEndAndIsSegmentLargeEnoughForCrossfade: function(){
        return !!this.getSequencerNodeToCrossfadeAtTheEndWith(false);
    },

    getSequencerNodeToCrossfadeAtTheEndWith : function(doNotCheckMinimumDurationForNeighborNode){
        if(!this.canLetFadeIfNeighborNodesAreDifferentFromOriginal()){
            return null;
        }
        let nextSequencerNodeToCrossfeadeAtTheEndWith = null;
        let node = this.getNext();
        if(this.getAudioSegment() && this.getAudioSegment().getTranscribedAudioInstance()){
            const minimumDurationInMillisecondsForCrossfade  = this.getApplyCrossfadeAtTheBeginingDurationInMilliseconds() + this.getApplyCrossfadeAtTheEndDurationInMilliseconds();
            if(!doNotCheckMinimumDurationForNeighborNode && (minimumDurationInMillisecondsForCrossfade > 0 && minimumDurationInMillisecondsForCrossfade >= this.getAudioSegment().getDuration())){
                return null;
            }
            while(node != null){
                if(node.getAudioSegment() && node.getAudioSegment().getDuration() > 0){
                    if(node.needAutoCrossfadeIfNeighborNodesAreDifferentFromOriginal()){
                        nextSequencerNodeToCrossfeadeAtTheEndWith = node;
                    }
                    node = null;
                }else{
                    node = node.getNext();
                }
                
            }
            if(nextSequencerNodeToCrossfeadeAtTheEndWith){
                const audioSegment = nextSequencerNodeToCrossfeadeAtTheEndWith.getAudioSegment();
                const areNextToEachOther = this.getAudioSegment().getTranscribedAudioInstance().areAudioSegmentTheClosestToEachOther(this.getAudioSegment(), audioSegment);
                if(!areNextToEachOther){
                    if(!doNotCheckMinimumDurationForNeighborNode && (minimumDurationInMillisecondsForCrossfade > 0 && minimumDurationInMillisecondsForCrossfade >= audioSegment.getDuration())){
                        return null;
                    }
                    return nextSequencerNodeToCrossfeadeAtTheEndWith;
                }
            }
        }else{
            return null;
        }
    },

    getApplyCrossfadeAtTheBegining : function(){
        const applyCrossfadeAtTheBeginingValue = this.get("applyCrossfadeAtTheBegining");
        if(applyCrossfadeAtTheBeginingValue == true || applyCrossfadeAtTheBeginingValue == false){
            return applyCrossfadeAtTheBeginingValue;
        }else{
            return null;
        }
    },

    getApplyCrossfadeAtTheEnd : function(){
        const applyCrossfadeAtTheEnd = this.get("applyCrossfadeAtTheEnd");
        if(applyCrossfadeAtTheEnd == true || applyCrossfadeAtTheEnd == false){
            return applyCrossfadeAtTheEnd;
        }else{
            return null;
        }
    },

    setApplyCrossfadeAtTheBegining : function(applyCrossfadeAtTheBegining){
        this.set("applyCrossfadeAtTheBegining",applyCrossfadeAtTheBegining);
    },

    setApplyCrossfadeAtTheEnd : function(applyCrossfadeAtTheEnd){
        this.set("applyCrossfadeAtTheEnd",applyCrossfadeAtTheEnd);
    },

    getApplyCrossfadeAtTheBeginingDurationInMilliseconds : function(){
        const applyCrossfadeAtTheBeginingDurationInMilliseconds = this.get("applyCrossfadeAtTheBeginingDurationInMilliseconds");
        if(applyCrossfadeAtTheBeginingDurationInMilliseconds > 0){
            return applyCrossfadeAtTheBeginingDurationInMilliseconds;
        }else{
            return 0;
        }
    },

    getApplyCrossfadeAtTheEndDurationInMilliseconds : function(){
        const applyCrossfadeAtTheEndDurationInMilliseconds = this.get("applyCrossfadeAtTheEndDurationInMilliseconds");
        if(applyCrossfadeAtTheEndDurationInMilliseconds > 0){
            return applyCrossfadeAtTheEndDurationInMilliseconds;
        }else{
            return 0;
        }
    },
    
    setApplyCrossfadeAtTheBeginingDurationInMilliseconds : function(applyCrossfadeAtTheBeginingDurationInMilliseconds){
        this.set("applyCrossfadeAtTheBeginingDurationInMilliseconds",applyCrossfadeAtTheBeginingDurationInMilliseconds);
    },

    setApplyCrossfadeAtTheEndDurationInMilliseconds : function(applyCrossfadeAtTheEndDurationInMilliseconds){
        this.set("applyCrossfadeAtTheEndDurationInMilliseconds",applyCrossfadeAtTheEndDurationInMilliseconds);
    },




    setNext : function(sequencerNode){
        if(!this._nextSequencerNode){
            this._nextSequencerNode = sequencerNode;
            this.set("_nextSequenceNodeCid", sequencerNode? sequencerNode.cid: null);
            sequencerNode._setPrevious(this);
        }else{
            if(this._nextSequencerNode === sequencerNode){
                return; // ignore redundant operation
            }
            throw "next node is already set and need to be cleared first"
        }
        this.trigger("nextNodeChange");
    },

    getNextSelectable :function(){
        /*const nextNode = this.getNext();
        const nextSelectable = null;
        while(nextNode && !nextSelectable){
            if(nextNode.getSelectable()){
                nextSelectable = nextNode;
            }else{
                nextNode = nextNode.getNext();
            }
        }
        return nextSelectable;*/

        return this.getNextNodeMatchingCondition((function(nextNode){
            return nextNode.getSelectable();
        }).bind(this));

    },


    getPreviousSelectable :function(){
        /*const previousNode = this.getPrevious();
        const previousSelectable = null;
        while(previousNode && !previousSelectable){
            if(previousNode.getSelectable()){
                previousSelectable = previousNode;
            }else{
                previousNode = previousNode.getPrevious();
            }
        }
        return previousSelectable;*/

        return this.getPreviousNodeMatchingCondition((function(previousNode){
            return previousNode.getSelectable();
        }).bind(this));

    },

    getContent : function(addSpaceAroundContent){
        return this.getAudioSegment(addSpaceAroundContent)? this.getAudioSegment().getContent(addSpaceAroundContent): null;
    },

    getSelectable : function(){
        return !this.get("not_selectable")
    },

    /**
     * Returns whether this node can be selected.
     * Alias for getSelectable() to match TypeScript interface.
     *
     * @returns {boolean} True if selectable
     */
    isSelectable : function(){
        return this.getSelectable();
    },

    setSelectable : function(selectable){
        this.set("not_selectable",!selectable)
    },

    getBeingProcessed : function(){
        return !!this.get("beingProcessed")
    },

    setBeingProcessed : function(beingProcessed){
        this.set("beingProcessed",!!beingProcessed)
    },

    setSelected : function(selected){
        this.set("selected",selected);
        if(selected){
            for(let i =0; i < this._startWrapNodes.length; i++){
                const startWrapNode = this._startWrapNodes[i];
                if(!startWrapNode.isHighlighted()){
                    startWrapNode.setHighlighted(true)
                }
            } 
            for(let i =0; i < this._endWrapNodes.length; i++){
                const endWrapNode = this._endWrapNodes[i];
                if(!endWrapNode.isHighlighted()){
                    endWrapNode.setHighlighted(true)
                }
            } 
        }else{
            for(let i =0; i < this._startWrapNodes.length; i++){
                const startWrapNode = this._startWrapNodes[i];
                if(startWrapNode.isHighlighted()){
                    startWrapNode.setHighlighted(false);
                }
            } 
            for(let i =0; i < this._endWrapNodes.length; i++){
                const endWrapNode = this._endWrapNodes[i];
                if(endWrapNode.isHighlighted()){
                    endWrapNode.setHighlighted(false)
                }
            } 
        }
    },

    setPlaying : function(playing){
        this.set("playing",playing);
    },

    setHighlighted : function(selected){
        this.set("highlighted",selected);
    },

    setHoverHighlighted : function(hoverHighlighted){
        this.set("hoverHighlighted",hoverHighlighted);
    },

    setSearchHightlighted : function(searchHightlighted){
        this.set("searchHightlighted",searchHightlighted);
    },

    setCommentHighlighted : function(commentHighlighted){
        this.set("commentHighlighted", commentHighlighted);
    },

    setCommentAnchored : function(commentAnchored){
        this.set("commentAnchored", commentAnchored);
    },

    

    clearNext: function(){
        if(this._nextSequencerNode){
            const nextSequencerNode = this._nextSequencerNode;
            this._nextSequencerNode._previousSequencerNode = null;
            this._nextSequencerNode = null;
            this.set("_nextSequenceNodeCid", null);
            nextSequencerNode.set("_previousSequenceNodeCid", null);
            nextSequencerNode.trigger("previousNodeChange");
            this.trigger("nextNodeChange");
        }
    },

    clearPrevious: function(){
        if(this._previousSequencerNode){
            const previousSequencerNode = this._previousSequencerNode;
            this._previousSequencerNode._nextSequencerNode = null;
            this._previousSequencerNode = null;
            this.set("_previousSequenceNodeCid", null);
            previousSequencerNode.set("_nextSequenceNodeCid", null);
            previousSequencerNode.trigger("nextNodeChange");
            this.trigger("previousNodeChange");
        }
        
    },

    isSelected : function(){
        return this.get("selected");
    },

    isPlaying : function(){
        return this.get("playing");
    },

    isHighlighted : function(){
        return this.get("highlighted");
    },

    isHoverHighlighted : function(){
        return this.get("hoverHighlighted");
    },

    isSearchHightlighted : function(){
        return this.get("searchHightlighted");
    },

    isCommentHighlighted : function(){
        return this.get("commentHighlighted");
    },

    isCommentAnchored : function(){
        return this.get("commentAnchored");
    },

    setAddedToSequencer : function(added){
        this.set("addedToSequenecer",added);
    },

    isAddedToSequencer : function(){
        return this.get("addedToSequenecer");
    },


    /*setReplacementNode : function(replacementNode){
        this.set("replacementNode",replacementNode);
    },

    getReplacementNode : function(){
        return this.get("replacementNode");
    },*/


    getNext : function(){
        return this._nextSequencerNode;
    },

    _setPrevious : function(sequencerNode){
        this._previousSequencerNode = sequencerNode;
        this.set("_previousSequenceNodeCid", sequencerNode? sequencerNode.cid: null);
        this.trigger("previousNodeChange");
    },

    getPrevious : function(){
        return this._previousSequencerNode;
    },

    insertBefore : function(sequencerNode, insertDoneViaClipboardPaste){
        const initialPreviousNode = sequencerNode.getPrevious();
        const originalPreviousNode = this.getPrevious();
        const originalNextNode = this.getNext();
        const originalStartWrapNodes = this._startWrapNodes ? this._startWrapNodes.slice(0) : [];
        const originalEndWrapNodes = this._endWrapNodes ? this._endWrapNodes.slice(0) : [];
        const originalInsertedByPasting = this.getInsertedByPasting ? this.getInsertedByPasting() : false;
        const wasAddedToSequencer = this.isAddedToSequencer();
        const sequencer = this.getSequencer();
        const wasFirstNodeBeforeInsert = !!(sequencer && sequencer.getFirstNode && sequencer.getFirstNode() === this);
        const wasLastNodeBeforeInsert = !!(sequencer && sequencer.getLastNode && sequencer.getLastNode() === this);
        if(initialPreviousNode){
            initialPreviousNode.clearNext();
            initialPreviousNode.setNext(this);
        }
        this.setNext(sequencerNode);
        this.setInsertedByPasting(!!insertDoneViaClipboardPaste);
        if(sequencerNode.isAddedToSequencer() && !this.isAddedToSequencer()){
            this.addToSequencer();
        }
        this._copyStartAndEndWrapNodes(initialPreviousNode);
        if(sequencerNode == this.getSequencer().getFirstNode()){
            this.getSequencer().setFirstNode(this);
        }
        this.trigger("wasInsertedBefore");
        try{
            sequencerNode.trigger("nodeInsertedBefore", this);
            if(initialPreviousNode){
                initialPreviousNode.trigger("nodeInsertedAfter", this);
            }
            const sequencerNodeToCrossfadeAtTheEndWith = this.getSequencerNodeToCrossfadeAtTheEndWith();
            if(sequencerNodeToCrossfadeAtTheEndWith && sequencerNodeToCrossfadeAtTheEndWith != sequencerNode &&  sequencerNodeToCrossfadeAtTheEndWith != initialPreviousNode){
                sequencerNodeToCrossfadeAtTheEndWith.trigger("crossfadeSequencerNodeNeighbourChange");
            }
            const sequencerNodeToCrossfadeAtTheBeginingWith = this.getSequencerNodeToCrossfadeAtTheBeginingWith();
            if(sequencerNodeToCrossfadeAtTheBeginingWith && sequencerNodeToCrossfadeAtTheBeginingWith != sequencerNode &&  sequencerNodeToCrossfadeAtTheBeginingWith != initialPreviousNode){
                sequencerNodeToCrossfadeAtTheBeginingWith.trigger("crossfadeSequencerNodeNeighbourChange");
            }
        }catch(error){
            this._rollbackFailedInsert({
                originalPreviousNode,
                originalNextNode,
                originalStartWrapNodes,
                originalEndWrapNodes,
                originalInsertedByPasting,
                wasAddedToSequencer,
                wasFirstNodeBeforeInsert,
                wasLastNodeBeforeInsert,
            });
            throw error;
        }
    },

    _copyStartAndEndWrapNodes : function(sequencerNodeToCopy){
        if(sequencerNodeToCopy){
            this._startWrapNodes = sequencerNodeToCopy._startWrapNodes.slice(0);
            this._endWrapNodes = sequencerNodeToCopy._endWrapNodes.slice(0);
        }
    },

    insertAfter : function(sequencerNode, insertDoneViaClipboardPaste){
        const initialNextNode = sequencerNode.getNext();
        const originalPreviousNode = this.getPrevious();
        const originalNextNode = this.getNext();
        const originalStartWrapNodes = this._startWrapNodes ? this._startWrapNodes.slice(0) : [];
        const originalEndWrapNodes = this._endWrapNodes ? this._endWrapNodes.slice(0) : [];
        const originalInsertedByPasting = this.getInsertedByPasting ? this.getInsertedByPasting() : false;
        const wasAddedToSequencer = this.isAddedToSequencer();
        const sequencer = this.getSequencer();
        const wasFirstNodeBeforeInsert = !!(sequencer && sequencer.getFirstNode && sequencer.getFirstNode() === this);
        const wasLastNodeBeforeInsert = !!(sequencer && sequencer.getLastNode && sequencer.getLastNode() === this);
        if(initialNextNode){
            initialNextNode.clearPrevious();
            this.setNext(initialNextNode);
        }
        sequencerNode.setNext(this);
        this.setInsertedByPasting(!!insertDoneViaClipboardPaste);
        if(sequencerNode.isAddedToSequencer() && !this.isAddedToSequencer()){
            this.addToSequencer();
        }
        this._copyStartAndEndWrapNodes(sequencerNode);
        if(sequencerNode == this.getSequencer().getLastNode()){
            this.getSequencer().setLastNode(this);
        }
        this.trigger("wasInsertedAfter");
        try{
            sequencerNode.trigger("nodeInsertedAfter", this);
            if(initialNextNode){
                initialNextNode.trigger("nodeInsertedBefore", this);
            }
            const sequencerNodeToCrossfadeAtTheEndWith = this.getSequencerNodeToCrossfadeAtTheEndWith();
            if(sequencerNodeToCrossfadeAtTheEndWith && sequencerNodeToCrossfadeAtTheEndWith != sequencerNode &&  sequencerNodeToCrossfadeAtTheEndWith != initialNextNode){
                sequencerNodeToCrossfadeAtTheEndWith.trigger("crossfadeSequencerNodeNeighbourChange");
            }
            const sequencerNodeToCrossfadeAtTheBeginingWith = this.getSequencerNodeToCrossfadeAtTheBeginingWith();
            if(sequencerNodeToCrossfadeAtTheBeginingWith && sequencerNodeToCrossfadeAtTheBeginingWith != sequencerNode &&  sequencerNodeToCrossfadeAtTheBeginingWith != initialNextNode){
                sequencerNodeToCrossfadeAtTheBeginingWith.trigger("crossfadeSequencerNodeNeighbourChange");
            }
        }catch(error){
            this._rollbackFailedInsert({
                originalPreviousNode,
                originalNextNode,
                originalStartWrapNodes,
                originalEndWrapNodes,
                originalInsertedByPasting,
                wasAddedToSequencer,
                wasFirstNodeBeforeInsert,
                wasLastNodeBeforeInsert,
            });
            throw error;
        }
    },

    _rollbackFailedInsert : function(options){
        const normalizedOptions = options || {};
        const originalPreviousNode = normalizedOptions.originalPreviousNode || null;
        const originalNextNode = normalizedOptions.originalNextNode || null;
        const originalStartWrapNodes = normalizedOptions.originalStartWrapNodes || [];
        const originalEndWrapNodes = normalizedOptions.originalEndWrapNodes || [];
        const wasAddedToSequencer = !!normalizedOptions.wasAddedToSequencer;
        const sequencer = this.getSequencer ? this.getSequencer() : null;
        const currentPreviousNode = this.getPrevious ? this.getPrevious() : null;
        const currentNextNode = this.getNext ? this.getNext() : null;

        if(currentNextNode){
            this.clearNext();
        }
        if(currentPreviousNode){
            this.clearPrevious();
        }
        if(currentPreviousNode && currentNextNode){
            currentPreviousNode.setNext(currentNextNode);
        }

        if(wasAddedToSequencer){
            if(originalPreviousNode){
                originalPreviousNode.setNext(this);
            }
            if(originalNextNode){
                this.setNext(originalNextNode);
            }
            if(sequencer){
                if(normalizedOptions.wasFirstNodeBeforeInsert && sequencer.setFirstNode){
                    sequencer.setFirstNode(this);
                }
                if(normalizedOptions.wasLastNodeBeforeInsert && sequencer.setLastNode){
                    sequencer.setLastNode(this);
                }
            }
        }else{
            if(sequencer){
                if(sequencer.getFirstNode && sequencer.getFirstNode() === this && sequencer.setFirstNode){
                    sequencer.setFirstNode(currentNextNode || null);
                }
                if(sequencer.getLastNode && sequencer.getLastNode() === this && sequencer.setLastNode){
                    sequencer.setLastNode(currentPreviousNode || null);
                }
            }
            if(this.isAddedToSequencer && this.isAddedToSequencer() && this.removeFromSequencer){
                this.removeFromSequencer();
            }
        }

        this._startWrapNodes = originalStartWrapNodes.slice(0);
        this._endWrapNodes = originalEndWrapNodes.slice(0);
        this.setInsertedByPasting(!!normalizedOptions.originalInsertedByPasting);
    },

    
    setHidden: function(hidden){
        this.set("hidden",hidden);
    },

    isHidden : function(){
        return this.get("hidden");
    },

    /**
     * Add a start wrap node to this sequencer node.
     *
     * SINGLE-LAYOUT CONSTRAINT (Layout Architecture Phase 1):
     * Enforces that a node can only be wrapped by ONE layout at a time.
     * If this is a layout wrap node and the node already has a different
     * layout wrap, the addition is rejected to maintain data integrity.
     *
     * @param {StartWrapSequencerNode} startWrapNode - The wrap node to add
     * @returns {boolean} True if added successfully, false if rejected
     *                    (either already exists or violates single-layout constraint)
     * @see getLayoutWrapNode - Gets the existing layout wrap for this node
     * @since Layout Architecture Phase 1
     */
    _isLayoutStartWrapNode : function(startWrapNode){
        return !!(
            startWrapNode
            && Utils.getInstance().isStartLayoutWrapSequencerNodeInstance
            && Utils.getInstance().isStartLayoutWrapSequencerNodeInstance(startWrapNode)
        );
    },

    _canAddStartWrapNode : function(startWrapNode){
        if(!startWrapNode){
            return false;
        }
        if(this._startWrapNodes.indexOf(startWrapNode) > -1){
            return true;
        }
        if(this._isLayoutStartWrapNode(startWrapNode)){
            const existingLayout = this.getLayoutWrapNode();
            if(existingLayout && existingLayout !== startWrapNode) {
                console.error('SequencerNode: Cannot add multiple layout wraps to same node');
                return false;
            }
        }
        return true;
    },

    canAttachWrapNodePair : function(startWrapNode, endWrapNode){
        if(!this._canAddStartWrapNode(startWrapNode)){
            return false;
        }
        if(endWrapNode && this._endWrapNodes.indexOf(endWrapNode) === -1){
            return this._canAddEndWrapNode(endWrapNode, startWrapNode);
        }
        return true;
    },

    addWrapNodePair : function(startWrapNode, endWrapNode){
        if(!this.canAttachWrapNodePair(startWrapNode, endWrapNode)){
            return false;
        }
        const startAlreadyPresent = this._startWrapNodes.indexOf(startWrapNode) !== -1;
        const endAlreadyPresent = !endWrapNode || this._endWrapNodes.indexOf(endWrapNode) !== -1;
        if(!startAlreadyPresent){
            this._startWrapNodes.push(startWrapNode);
        }
        if(endWrapNode && !endAlreadyPresent){
            this._endWrapNodes.push(endWrapNode);
        }
        if(!startAlreadyPresent && typeof startWrapNode.onWrappedNodeAdded === "function"){
            startWrapNode.onWrappedNodeAdded(this);
        }
        return true;
    },

    addStartWrapNode : function(startWrapNode){
        if(!this._canAddStartWrapNode(startWrapNode)){
            return false;
        }
        if(this._startWrapNodes.indexOf(startWrapNode) === -1){
            this._startWrapNodes.push(startWrapNode);

            // Notify the wrap node that this node was added to its wrapped content
            if (typeof startWrapNode.onWrappedNodeAdded === 'function') {
                startWrapNode.onWrappedNodeAdded(this);
            }
            return true;
        }
        return false; // Already exists
    },

    needAutoCrossfadeIfNeighborNodesAreDifferentFromOriginal : function(){
        return false;
    },

    canLetFadeIfNeighborNodesAreDifferentFromOriginal : function(){
        return false;
    },


    removeStartWrapNode : function(startWrapNode){
        const indexToRemove = this._startWrapNodes.indexOf(startWrapNode);
        if(indexToRemove > -1){
            this._startWrapNodes.splice(indexToRemove, 1);
        }
    },

    _canAddEndWrapNode : function(endWrapNode, pairedStartWrapNode){
        if(!endWrapNode){
            return false;
        }
        if(this._endWrapNodes.indexOf(endWrapNode) > -1){
            return true;
        }
        const startWrapNode = endWrapNode.getStartWrapSequencerNode ? endWrapNode.getStartWrapSequencerNode() : null;
        if(
            this._isLayoutStartWrapNode(startWrapNode)
            && this._startWrapNodes.indexOf(startWrapNode) === -1
            && startWrapNode !== pairedStartWrapNode
        ){
            console.error('SequencerNode: Cannot add layout end wrap without matching start wrap');
            return false;
        }
        return true;
    },

    addEndWrapNode : function(endWrapNode){
        if(!this._canAddEndWrapNode(endWrapNode)){
            return false;
        }
        if(this._endWrapNodes.indexOf(endWrapNode) === -1){
            this._endWrapNodes.push(endWrapNode);
            return true;
        }
        return false;
    },

    removeEndWrapNode : function(endWrapNode){
        const indexToRemove = this._endWrapNodes.indexOf(endWrapNode);
        if(indexToRemove > -1){
            this._endWrapNodes.splice(indexToRemove, 1);
        }
    },

    /*setDefaultVolumeGainFilter : function(volumeGainFilter){
        if(this._audioSegment){
            this._audioSegment.setDefaultVolumeGainFilter(volumeGainFilter);
        }
    },

    getDefaultVolumeGainFilter : function(){
        if(this._audioSegment){
            return this._audioSegment.getDefaultVolumeGainFilter();
        }
    },*/


    /*addAudioFilter : function(filter){
        if(this._audioSegment){
            this._audioSegment.addAudioFilter(filter);
        }
    },

    getAudioFilters : function(){
        if(this._audioSegment){
            return this._audioSegment.getAudioFilters();
        }
    },*/

    addAudioFilter : function(filter){
        this._audioFilters.push(filter);
    },

    getAudioFilters : function(){
        return this._audioFilters;
    },

    /*addBuiltInAudioFilter : function(name, filter){
        if(this._audioSegment){
            this._audioSegment.addAudioFilter(filter);
        }
    },

    getBuiltInAudioFilterByName : function(name){
        return this._uniqueNameToBuiltInFilter[name];
    },*/

    addBuiltInAudioFilter : function(name, filter){
        this._uniqueNameToBuiltInFilter[name] = filter;
        this.addAudioFilter(filter);
    },

    getBuiltInAudioFilterByName : function(name){
        return this._uniqueNameToBuiltInFilter[name];
    },

    getStartWrapNodes : function(){
        return this._startWrapNodes;
    },

    getEndWrapNodes : function(){
        return this._endWrapNodes;
    },

    /**
     * Get the layout wrap node that wraps this sequencer node (if any).
     *
     * SINGLE-LAYOUT CONSTRAINT (Layout Architecture Phase 1):
     * A node can only be wrapped by ONE layout at a time. This method
     * scans the _startWrapNodes array to find any layout wrap node.
     *
     * Used by:
     * - MergedSequencerNodeSchedulingInfo.canBeMerged() for layout discontinuity check
     * - SequencerNodeSchedulingInfo.getLayoutChunkPosition() for animation decisions
     * - VisualElementDetector.nodeIsInsideLayout() to prevent double-rendering
     *
     * @returns {StartLayoutWrapSequencerNode|null} The layout wrap node or null if not wrapped
     * @see addStartWrapNode - Enforces single-layout constraint during wrap addition
     * @since Layout Architecture Phase 1
     */
    getLayoutWrapNode: function() {
        if (!this._startWrapNodes || this._startWrapNodes.length === 0) {
            return null;
        }
        for (let i = 0; i < this._startWrapNodes.length; i++) {
            const wrapNode = this._startWrapNodes[i];
            if (this._isLayoutStartWrapNode(wrapNode)) {
                return wrapNode;
            }
        }
        return null;
    },

    getNextNodeMatchingCondition: function(conditionFunctionBoundToThisSequencerNode, numberOfNodesToSkipMatchingCondition, returnCurrentNodeIfNoNodeIsMatching){
        if(this.getNext() && this.getSequencer()){
            if(!numberOfNodesToSkipMatchingCondition){
                numberOfNodesToSkipMatchingCondition = 0;
            }
            let nextSequencerNodeToReturn = null;
            let nextSequencerNode =  this.getNext();
            while(numberOfNodesToSkipMatchingCondition >= 0 && nextSequencerNode){
                if(conditionFunctionBoundToThisSequencerNode(nextSequencerNode)){
                    nextSequencerNodeToReturn  = nextSequencerNode;
                    numberOfNodesToSkipMatchingCondition = numberOfNodesToSkipMatchingCondition - 1;
                }
                nextSequencerNode = nextSequencerNode.getNext();
            }
            if(nextSequencerNodeToReturn){
                return nextSequencerNodeToReturn;
            }else{
                return returnCurrentNodeIfNoNodeIsMatching? this: null;
            }
        }
        if(returnCurrentNodeIfNoNodeIsMatching){
            return this;
        }else{
            return null;
        }
    },

    getPreviousNodeMatchingCondition: function(conditionFunctionBoundToThisSequencerNode, numberOfNodesToSkipMatchingCondition, returnCurrentNodeIfNoNodeIsMatching){
        if(this.getPrevious() && this.getSequencer()){
            if(!numberOfNodesToSkipMatchingCondition){
                numberOfNodesToSkipMatchingCondition = 0;
            }
            let previousSequencerNodeToReturn = null;
            let previousSequencerNode =  this.getPrevious();
            while(numberOfNodesToSkipMatchingCondition >= 0 && previousSequencerNode){
                if(conditionFunctionBoundToThisSequencerNode(previousSequencerNode)){
                    previousSequencerNodeToReturn  = previousSequencerNode;
                    numberOfNodesToSkipMatchingCondition = numberOfNodesToSkipMatchingCondition - 1;
                }
                previousSequencerNode = previousSequencerNode.getPrevious();
            }
            if(previousSequencerNodeToReturn){
                return previousSequencerNodeToReturn;
            }else{
                return returnCurrentNodeIfNoNodeIsMatching? this: null;
            }
        }
        if(returnCurrentNodeIfNoNodeIsMatching){
            return this;
        }else{
            return null;
        }
    },

    getNextVisibleSequencerNode : function(){
        return this.getNextNodeMatchingCondition((function(nextNode){
            return this.getSequencer().isSequencerNodeVisible(nextNode);
        }).bind(this));
    },

    getPreviousVisibleSequencerNode : function(sequencerNode){
        return this.getPreviousNodeMatchingCondition((function(previousNode){
            return this.getSequencer().isSequencerNodeVisible(previousNode);
        }).bind(this));
    },

    getNextVisibleSequencerNodeWithDuration : function(){
        return this.getNextNodeMatchingCondition((function(nextNode){
            return this.getSequencer().isSequencerNodeVisible(nextNode) && nextNode.getDuration() > 0
        }).bind(this));
    },

    getPreviousVisibleSequencerNodeWithDuration : function(sequencerNode){
        return this.getPreviousNodeMatchingCondition((function(previousNode){
            return this.getSequencer().isSequencerNodeVisible(previousNode) && previousNode.getDuration() > 0
        }).bind(this));
    },

    /**
     * Whether this node can be associated with any speaker.
     * @returns {boolean} True if can be associated with any speaker
     * @deprecated Use canBeAssociatedWithAnySpeaker() for correct spelling
     */
    canBeAssociactedWithAnySpeaker : function(){
        return this.getAudioSegment() && this.getAudioSegment().canBeAssociactedWithAnySpeaker()? this.getAudioSegment().canBeAssociactedWithAnySpeaker(): false;
    },

    /**
     * Whether this node can be associated with any speaker.
     * Alias for canBeAssociactedWithAnySpeaker() with correct spelling.
     * @returns {boolean} True if can be associated with any speaker
     */
    canBeAssociatedWithAnySpeaker : function(){
        return this.canBeAssociactedWithAnySpeaker();
    },

    /**
     * Whether this node must have a speaker assigned.
     * @returns {boolean} True if must have a speaker assigned
     * @deprecated Use needToBeAssociatedWithASpeaker() for correct spelling
     */
    needToBeAssociactedWithASpeaker : function(){
        return this.getAudioSegment() && this.getAudioSegment().needToBeAssociactedWithASpeaker()? this.getAudioSegment().needToBeAssociactedWithASpeaker(): false;
    },

    /**
     * Whether this node must have a speaker assigned.
     * Alias for needToBeAssociactedWithASpeaker() with correct spelling.
     * @returns {boolean} True if must have a speaker assigned
     */
    needToBeAssociatedWithASpeaker : function(){
        return this.needToBeAssociactedWithASpeaker();
    },

    // Nodes can opt into paragraph boundaries or choose the paragraph kind they want to render inside.
    getParagraphLayoutBehavior : function(){
        return ParagraphLayoutBehavior.create();
    },

    getSpeakerInfo : function(){
        return this.getAudioSegment() && this.getAudioSegment().getSpeakerInfo()? this.getAudioSegment().getSpeakerInfo(): null;
    },

    getJSONDescriptionForAIAnalysis : function(){
        const cloneJson = this.cloneJSON();
        const speakerInfo = this.getSpeakerInfo();
        return {id: this.cid, type: cloneJson.type, content: this.getContent(), speakerLabel : speakerInfo?.getSpeakerLabel() ,speakerId : speakerInfo?.getSpeakerId(), duration: this.getAudioSegment()?.getDuration() }

    },

    // ============================================================
    // PREPROCESSING SYSTEM
    // Base methods for preprocessing tasks (e.g., face detection)
    // Override in subclasses that need preprocessing
    // ============================================================

    /**
     * Get the preprocessing tasks for this node.
     * Override in subclasses to return an array of preprocessing task classes.
     *
     * @returns {Array} Array of preprocessing task classes (empty by default)
     */
    getPreprocessingTasks: function() {
        return [];
    },

    /**
     * Check if this node has outstanding preprocessing that needs to run.
     * Override in subclasses to provide custom logic for cache invalidation.
     *
     * @returns {boolean} True if preprocessing needs to run
     */
    hasOutstandingPreprocessing: function() {
        // Check if node has any preprocessing tasks
        const tasks = this.getPreprocessingTasks();
        if (tasks.length === 0) {
            return false;
        }

        // Check preprocessing status - if in a terminal state, no NEW preprocessing needed
        // Terminal states that should skip preprocessing:
        // - 'complete': Successfully processed
        // - 'cors_skipped': Skipped due to cross-origin content, using defaults
        // - 'timeout': Timed out, using defaults
        // - 'failed': Failed, using defaults
        const status = this.getPreprocessingStatus();
        const skipStatuses = ['complete', 'cors_skipped', 'timeout', 'failed'];
        return !skipStatuses.includes(status);
    },

    /**
     * Run all preprocessing tasks for this node.
     * Override in subclasses to provide custom preprocessing logic.
     *
     * @param {Object} options - Options for preprocessing
     * @param {PreprocessingManager} options.manager - The preprocessing manager
     * @returns {Promise} Resolves when all preprocessing is complete
     */
    runPreprocessing: function(options) {
        // RSVP is imported at the top of this file
        const tasks = this.getPreprocessingTasks();

        if (tasks.length === 0) {
            return RSVP.Promise.resolve();
        }

        // Run each task that needs processing
        // Note: needsProcessing may be async (for IndexedDB persistence checking)
        const promises = tasks.map(async (TaskClass) => {
            // Check if task needs to run (supports both sync and async needsProcessing)
            if (TaskClass.needsProcessing) {
                const needsToRun = await Promise.resolve(TaskClass.needsProcessing(this));
                if (!needsToRun) {
                    return;
                }
            }

            // Execute the task
            const result = await TaskClass.execute(this, options);
            // Store the result
            if (TaskClass.storeResult) {
                TaskClass.storeResult(this, result);
            }
        });

        return RSVP.Promise.all(promises);
    },

    /**
     * Get the preprocessing status for this node.
     * Override in subclasses to provide custom status tracking.
     *
     * @returns {string} Status: 'pending' | 'running' | 'complete' | 'failed'
     */
    getPreprocessingStatus: function() {
        return this.get('preprocessing_status') || 'pending';
    },

    /**
     * Set the preprocessing status for this node.
     *
     * @param {string} status - The new status
     */
    setPreprocessingStatus: function(status) {
        this.set('preprocessing_status', status);
    },

    // ============================================================
    // WRAP NODE CHANGE BROADCASTING SYSTEM
    // Allows nodes to notify their containing wrap nodes when
    // properties change, enabling bidirectional synchronization.
    // ============================================================

    /**
     * Broadcast a property change to all wrap nodes that contain this node.
     * Wrap nodes can implement onWrappedNodeChanged() to receive these notifications.
     *
     * @param {string} propertyCategory - Category of the property (from WrappedNodePropertyCategory)
     * @param {string} propertyName - The name of the property that changed
     * @param {*} newValue - The new value of the property
     * @param {*} oldValue - The previous value of the property
     * @private
     */
    _broadcastChangeToWrapNodes: function(propertyCategory, propertyName, newValue, oldValue) {
        if (!this._startWrapNodes || this._startWrapNodes.length === 0) {
            return;
        }

        const startWrapNodes = this._startWrapNodes.slice();
        for (let i = 0; i < startWrapNodes.length; i++) {
            const wrapNode = startWrapNodes[i];
            // Defensive check - only call if the method exists
            if (wrapNode && typeof wrapNode.onWrappedNodeChanged === 'function') {
                wrapNode.onWrappedNodeChanged(this, propertyCategory, propertyName, newValue, oldValue);
            }
        }
    },

    /**
     * Enable change broadcasting by listening to Backbone change events.
     * When any property changes, this determines the category and broadcasts
     * to wrap nodes.
     *
     * @private
     */
    _enableChangeBroadcasting: function() {
        // Listen to all change events on this model
        this.listenTo(this, 'change', (function(model, options) {
            // Get the changed attributes
            const changedAttributes = this.changedAttributes();
            if (!changedAttributes) {
                return;
            }

            // Broadcast each changed property to wrap nodes
            for (const propertyName in changedAttributes) {
                if (Object.prototype.hasOwnProperty.call(changedAttributes, propertyName)) {
                    const newValue = changedAttributes[propertyName];
                    const oldValue = this.previous(propertyName);
                    const category = getPropertyCategory(propertyName);

                    this._broadcastChangeToWrapNodes(category, propertyName, newValue, oldValue);
                }
            }
        }).bind(this));

        // Also listen to change:audioSegment events triggered by _listenToChangesOnAudioSegment
        // when the underlying audio segment mutates. This ensures wrap nodes are notified
        // of audio segment content changes (e.g., when video content within a segment changes).
        this.listenTo(this, 'change:audioSegment', (function(model) {
            if(this.hasChanged && this.hasChanged('audioSegment')){
                return;
            }
            const audioSegment = this.getAudioSegment();
            this._broadcastChangeToWrapNodes(
                WrappedNodePropertyCategory.AUDIO_SEGMENT,
                'audioSegment',
                audioSegment,
                audioSegment // oldValue is same since segment mutated in place
            );
        }).bind(this));
    },

    /**
     * Notify wrap nodes before this node is removed from the sequencer.
     * This allows wrap nodes to update their listeners and clean up references.
     * Should be called before removing the node from the timeline.
     *
     * @returns {void}
     */
    notifyWrapNodesBeforeRemoval: function() {
        // Notify start wrap nodes
        if (this._startWrapNodes && this._startWrapNodes.length > 0) {
            const startWrapNodes = this._startWrapNodes.slice();
            for (let i = 0; i < startWrapNodes.length; i++) {
                const wrapNode = startWrapNodes[i];
                if (wrapNode && typeof wrapNode.onWrappedNodeBeforeRemoval === 'function') {
                    wrapNode.onWrappedNodeBeforeRemoval(this);
                }
            }
        }

        // Also notify end wrap nodes in case they track this node
        if (this._endWrapNodes && this._endWrapNodes.length > 0) {
            const endWrapNodes = this._endWrapNodes.slice();
            for (let i = 0; i < endWrapNodes.length; i++) {
                const wrapNode = endWrapNodes[i];
                if (wrapNode && typeof wrapNode.onWrappedNodeBeforeRemoval === 'function') {
                    wrapNode.onWrappedNodeBeforeRemoval(this);
                }
            }
        }
    },

});

SequencerNode.CLASSNAME =  CLASSNAME;
export default SequencerNode;
